/**
 * Season Mode Configuration
 * 
 * Controls whether the site leads with landscaping or snow content.
 * Change this single value to flip the entire site's seasonal focus.
 * 
 * 'landscaping' - Summer/Spring mode (default April-October)
 * 'snow' - Winter mode (November-March)
 */

export const SEASON_MODE = 'landscaping';

// Season-specific content configurations
export const seasonConfig = {
  landscaping: {
    // SEO
    homeTitle: 'Landscaping & Lawn Care in Menomonee Falls, WI | WayBetter',
    homeDescription: 'Professional landscaping, lawn mowing, hardscapes, tree service & mulching in Menomonee Falls, Brookfield, New Berlin & Milwaukee. Free estimates. Call (414) 795-3610.',
    homeH1: 'Landscaping & Lawn Care in Menomonee Falls',
    
    // Hero content
    heroTagline: 'Dependable • Honest • Professional',
    heroCopy: 'Serving Metro Milwaukee since 2014. SBE-certified and locally operated, we deliver reliable lawn maintenance, landscape design, and hardscape installation you can count on.',
    heroImage: '/images/home-hero.webp',
    
    // Service cards in priority order for landscaping season
    primaryServices: [
      {
        id: 'lawn-maintenance',
        title: 'Lawn Maintenance & Mowing',
        description: 'Weekly mowing, edging, trimming, and fertilization to keep your lawn looking pristine all season.',
        image: '/images/summer-2.webp',
        link: '/services/lawn-maintenance',
      },
      {
        id: 'landscaping-design',
        title: 'Landscaping & Design',
        description: 'Custom landscape design, plantings, bed installation, and seasonal color to transform your property.',
        image: '/images/spring-1.webp',
        link: '/services/landscape-design',
      },
      {
        id: 'hardscapes',
        title: 'Hardscapes & Patios',
        description: 'Patios, walkways, retaining walls, and outdoor living spaces that add value and curb appeal.',
        image: '/images/hardscape-1.webp',
        link: '/services/hardscape',
      },
      {
        id: 'tree-service',
        title: 'Tree Service',
        description: 'Professional tree trimming, pruning, removal, and stump grinding for healthy, safe trees.',
        image: '/images/services/tree-service-1.webp',
        link: '/services/tree-service',
      },
      {
        id: 'mulching',
        title: 'Mulching & Bed Cleanups',
        description: 'Fresh mulch installation, bed edging, and seasonal cleanups to keep beds looking sharp.',
        image: '/images/spring-2.webp',
        link: '/residential',
      },
      {
        id: 'power-sweeping',
        title: 'Power Sweeping / Lot Sweeping',
        description: 'Commercial parking lot sweeping and debris removal to keep your business looking professional.',
        image: '/images/commercial-1.webp',
        link: '/services/power-sweeping',
      },
    ],
    
    // Show snow as footer mention only
    snowMention: true,
  },
  
  snow: {
    // SEO
    homeTitle: 'WayBetter Landscaping & Snowplowing | Metro Milwaukee Lawn Care & Snow Removal',
    homeDescription: 'Professional landscaping, lawn care, hardscaping, and 24/7 snow removal services in Metro Milwaukee. SBE-certified, serving Brookfield, Menomonee Falls, Waukesha & surrounding areas. Free estimates!',
    homeH1: 'Milwaukee Lawn Care & Menomonee Falls Landscaping Experts',
    
    // Hero content
    heroTagline: 'Dependable • Honest • Professional',
    heroCopy: 'Serving Metro Milwaukee since 2014. SBE-certified and locally operated, we deliver the kind of consistent residential lawn maintenance and commercial snow removal service you don\'t have to chase down.',
    heroImage: '/images/home-hero.webp',
    
    // Service cards for snow season
    primaryServices: [
      {
        id: 'residential',
        title: 'Residential Landscaping',
        description: 'Weekly mowing, bed maintenance, landscape design, and seasonal clean-ups for homeowners.',
        image: '/images/home-residential.webp',
        link: '/residential',
      },
      {
        id: 'commercial',
        title: 'Commercial Services',
        description: 'Full grounds maintenance, hardscapes, and lot sweeping for businesses and property managers.',
        image: '/images/commercial-1.webp',
        link: '/commercial',
      },
      {
        id: 'hardscapes',
        title: 'Hardscapes',
        description: 'Patios, walkways, retaining walls, and outdoor living spaces that add value and curb appeal.',
        image: '/images/hardscape-1.webp',
        link: '/residential',
      },
      {
        id: 'snow',
        title: 'Snow & Ice Management',
        description: '24/7 snow plowing, sidewalk clearing, de-icing, and snow hauling to keep your property safe.',
        image: '/images/winter-3.webp',
        link: '/snow-removal',
      },
    ],
    
    snowMention: false,
  }
};

// Get current season config
export const getCurrentSeasonConfig = () => seasonConfig[SEASON_MODE];

// City pages for internal linking
export const cityPages = [
  { name: 'New Berlin', slug: 'new-berlin', path: '/landscaping-new-berlin-wi' },
  { name: 'Brookfield', slug: 'brookfield', path: '/landscaping-brookfield-wi' },
  { name: 'Menomonee Falls', slug: 'menomonee-falls', path: '/landscaping-menomonee-falls-wi' },
  { name: 'Waukesha', slug: 'waukesha', path: '/landscaping-waukesha-wi' },
  { name: 'Muskego', slug: 'muskego', path: '/landscaping-muskego-wi' },
  { name: 'Milwaukee', slug: 'milwaukee', path: '/landscaping-milwaukee-wi' },
  { name: 'Greenfield', slug: 'greenfield', path: '/landscaping-greenfield-wi' },
];

// Blog posts for internal linking
export const blogPostsForLinking = [
  { title: 'Spring Lawn Prep Checklist', path: '/blog/spring-lawn-prep-checklist-wisconsin' },
  { title: 'How Often to Mow Your Lawn', path: '/blog/how-often-should-you-mow-your-lawn' },
  { title: 'Retaining Walls 101', path: '/blog/retaining-walls-101' },
  { title: 'Reduce Snow Removal Costs', path: '/blog/reduce-snow-removal-costs' },
  { title: 'When to Aerate in Wisconsin', path: '/blog/when-to-aerate-lawn-wisconsin' },
  { title: 'Mulch vs Rock for Beds', path: '/blog/mulch-vs-rock-landscape-beds' },
];

export default SEASON_MODE;
